import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { ambulanceDispatchApi } from '@/domains/traffic-monitoring/apis/ambulance-dispatch';
import type { AmbulanceDriverTypes } from '@/domains/traffic-monitoring/constants/listEvent.constant';
import type {
  AmbulanceListItemView,
  ReportAmbulanceItem,
  ReportAmbulanceListData,
  ReportDispatchRequestBody,
  ReportHeaderData,
  ReportHeaderView,
} from '@/domains/traffic-monitoring/types/ambulance-dispatch.types';
import { useAuthStore } from '@/store/authStore';

/**
 * Wires AmbulanceDispatch.tsx (PC0202005) to api-dsh:
 *  - GET /ambulance-report/{report_id}/ambulance (report-scoped, distance-ranked fleet)
 *  - GET /report/{report_id} (used here only for number_of_victims in the header)
 *  - POST /report/dispatch (submit selected ambulances)
 *
 * When reportId is undefined (no ?reportId= on the URL), both queries stay
 * disabled and the page falls back to its local mock data - see
 * SmartCityDashboard.tsx's displayMarkers convention.
 */

const STATUS_MAP: Record<ReportAmbulanceItem['ambulance_status'], AmbulanceDriverTypes> = {
  AMB_ST_STBY: 'standby',
  AMB_ST_ONGO: 'ongoing',
  AMB_ST_MNT: 'maintenance',
  AMB_ST_OFF: 'offline',
  AMB_ST_REQ: 'requested',
};

// Same status the TTL auto-expiry writes on timeout (expire_stale_dispatch_
// requests.py: "same shape as an explicit crew reject") - so this one check
// covers both a manual decline and an inactivity timeout.
const REJECTED_STATUS = 'AMB_RPT_ST_REJ';

const normalizeAmbulances = (data: ReportAmbulanceListData | null): AmbulanceListItemView[] => {
  if (!data?.items) return [];
  return data.items.map((item, index) => ({
    id: item.ambulance_id,
    plateNumber: item.police_number ?? item.serial_no ?? '-',
    hospital: item.health_care_facility_name ?? '-',
    pic: item.pic_name ?? '-',
    type: item.ambulance_type_name ?? item.vehicle_type ?? '-',
    distance: item.distance_km != null ? `${item.distance_km} Km` : 'Unknown',
    ambulanceDriverTypes: STATUS_MAP[item.ambulance_status] ?? 'offline',
    // The endpoint default-sorts nearest-first, so the first row with a known
    // distance is the nearest candidate.
    nearest: index === 0 && item.distance_m != null,
    ownershipType: item.ambulance_ownership_type_name ?? item.ambulance_ownership_type,
    disabled: item.assignment?.ambulance_report_status === REJECTED_STATUS,
    // Already has a live (non-rejected) dispatch request on THIS report -
    // AmbulanceDispatch.tsx pre-fills a slot with these instead of leaving
    // them for the operator to re-pick, so a partially-accepted report (e.g.
    // 3-of-5) only asks for the remaining, actually-empty slots.
    isActiveAssignment:
      item.assignment != null && item.assignment.ambulance_report_status !== REJECTED_STATUS,
  }));
};

// "Nothing left to do here" now means every victim's slot already has a
// live (non-rejected) assignment - NOT just "someone accepted". A report can
// be 3-of-5 accepted with 2 still rejected and needing a Re-Dispatch
// Replacement pick, and that report must still be reachable on this page;
// the old "any accepted" check redirected it straight to the details page
// out from under the operator the moment the first acceptance came in.
const countActiveAssignments = (data: ReportAmbulanceListData | null): number =>
  (data?.items ?? []).filter(
    (item) => item.assignment != null && item.assignment.ambulance_report_status !== REJECTED_STATUS
  ).length;

const normalizeReportHeader = (data: ReportHeaderData | null): ReportHeaderView | null => {
  if (!data) return null;
  return {
    reportCode: data.report_code_id ?? '-',
    incidentLocation: data.incident_location ?? '-',
    victimsCount: data.number_of_victims ?? 1,
  };
};

export interface UseAmbulanceDispatchOptions {
  /** 'status' asks the backend to sort by cm_code.sort_order (AMB_ST_*). 'owner'
   *  has no backend sort mode, so it's applied client-side after normalizing. */
  sortBy?: 'owner' | 'status';
  /** ILIKE match on police_number / serial_no / health_care_facility.name. */
  keyword?: string;
}

export const useAmbulanceDispatch = (
  reportId?: number,
  options: UseAmbulanceDispatchOptions = {}
) => {
  const { sortBy = 'owner', keyword } = options;
  const queryClient = useQueryClient();
  const enabled = reportId != null;
  const serverSort = sortBy === 'status' ? 'status' : undefined;
  const accountType = useAuthStore((s) => s.account_type);
  const facilityId = useAuthStore((s) => s.health_care_facility_id);

  const { ambulances, reportHeader, isLoading, isRefreshing, isError, refetch, alreadyDispatched } =
    useQueries({
      queries: [
        {
          queryKey: ['reportAmbulanceList', reportId, serverSort, keyword],
          queryFn: async () => {
            const res = await ambulanceDispatchApi.getReportAmbulanceList(reportId as number, {
              sort: serverSort,
              keyword: keyword || undefined,
              // Server-enforced for a healthcare account regardless, sent
              // anyway so this account's own view reflects its scope.
              ...(accountType === 'healthcare' && facilityId != null
                ? { health_care_facility_id: facilityId }
                : {}),
            });
            return res?.data ?? null;
          },
          enabled,
        },
        {
          queryKey: ['reportHeader', reportId],
          queryFn: async () => {
            const res = await ambulanceDispatchApi.getReportDetail(reportId as number);
            return res?.data?.report ?? null;
          },
          enabled,
        },
      ],
      combine: ([listQuery, headerQuery]) => {
        const rawList = (listQuery.data as ReportAmbulanceListData | null) ?? null;
        const rawHeader = (headerQuery.data as ReportHeaderData | null) ?? null;
        const items = normalizeAmbulances(rawList);
        const ambulances =
          sortBy === 'owner'
            ? [...items].sort((a, b) => a.ownershipType.localeCompare(b.ownershipType))
            : items;
        return {
          ambulances,
          reportHeader: normalizeReportHeader(rawHeader),
          // The list and header queries resolve independently - if the list
          // lands first, rawHeader is still null here for a frame or two.
          // Falling back to "1 victim" in that window made 3 active
          // assignments >= 1 trivially true and fired the redirect below
          // before the real victim count (e.g. 5) ever arrived. Don't claim
          // "fully staffed" until the victim count is actually known.
          alreadyDispatched:
            rawHeader != null &&
            countActiveAssignments(rawList) >= (rawHeader.number_of_victims ?? 1),
          isLoading: listQuery.isLoading || headerQuery.isLoading,
          // isLoading is only true on the very first fetch (no cached data yet) -
          // a manual refetch() of already-loaded data only flips isFetching, so the
          // refresh button needs this to show it did something.
          isRefreshing: listQuery.isFetching || headerQuery.isFetching,
          // A 403 (report outside a healthcare account's scope) lands here -
          // AmbulanceDispatch.tsx renders an explicit "not accessible" state
          // instead of an indefinite spinner.
          isError: listQuery.isError || headerQuery.isError,
          refetch: () => Promise.all([listQuery.refetch(), headerQuery.refetch()]),
        };
      },
    });

  const dispatchMutation = useMutation({
    mutationFn: ({
      ambulanceIds,
      numberOfVictims,
    }: {
      ambulanceIds: number[];
      // Only set when this dispatch fills a slot added beyond the report's
      // original victim count (see AmbulanceDispatch.tsx's "Add Ambulance"
      // flow) - the DB's number_of_victims only grows once that extra slot
      // actually dispatches, not when the operator merely adds the slot.
      numberOfVictims?: number;
    }) => {
      const body: ReportDispatchRequestBody = {
        ambulance_ids: ambulanceIds,
        report_detail: {
          report_id: reportId as number,
          ...(numberOfVictims != null ? { number_of_victims: numberOfVictims } : {}),
        },
      };
      return ambulanceDispatchApi.postReportDispatch(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportAmbulanceList', reportId] });
      queryClient.invalidateQueries({ queryKey: ['reportHeader', reportId] });
    },
  });

  return {
    ambulances,
    reportHeader,
    alreadyDispatched,
    isLoading,
    isRefreshing,
    isError,
    refetch,
    dispatch: dispatchMutation.mutate,
    isDispatching: dispatchMutation.isPending,
  };
};
